<template>
  <main class="pt-8 lg_pt-20" :class="{ 'investor-banner-visible': showInvestorBanner }">
    <LoadingSpinner v-if="state.loading" />
    <div v-else-if="state.company" class="content">
      <VBanner v-if="showInvestorBanner" class="z-20 top-24" @on-dismiss="showInvestorBanner = false">
        You are viewing your page as an investor
      </VBanner>
      <CompaniesCompanyPageNavigation
        :company="state.company"
        :show="showNavigation"
        :activity="state.activities.length"
        :management="state.people.length"
        :assets="state.vrifiedAssets.length || state.otherAssets.length"
        :press-releases="state.posts.results.length"
        :stock="state.stocks.length"
        :investor-decks="state.investorDecks.length"
      />
      <ClientOnly>
        <CompaniesCompanySection
          class="container"
          :company="state.company"
          :regions="state.regions"
          :commodities="state.commodities"
          :class="[showInvestorBanner ? 'pt-8 lg_pt-0' : '']"
        />
        <div class="split-view lg_relative">
          <div id="company-card" class="fixed bottom-0 left-0 right-0 z-10 pointer-events-none lg_absolute lg_w-full lg_h-full">
            <div
              class="w-full lg_container lg_sticky lg_z-10 lg_flex lg_justify-end"
              :class="showInvestorBanner ? 'lg_top-56' : 'lg_top-32'"
            >
              <CompaniesCompanyCard class="pointer-events-auto lg_relative lg_w-100" :company="state.company" />
            </div>
          </div>
          <div class="section-gradient">
            <CompaniesFeaturedDeckSection
              id="featured-deck"
              class="container"
              :company="state.company"
              :featured-deck="state.featuredDeck"
            />
          </div>
          <hr ref="navigationScrollMarker" class="m-0 border-none" />
          <div v-if="state.people.length" class="striped">
            <CompaniesManagementSection id="management" class="container" :people="state.people" />
          </div>
          <div v-if="state.activities.length" class="bg-copper-100 py-28">
            <CompaniesActivitySection id="activity" class="container" :activities="state.activities" data-testid="activity-section" />
          </div>
        </div>
        <div class="post-floating-widget">
          <CompaniesAssetsSectionVrified :assets="state.vrifiedAssets" />
          <CompaniesAssetsSectionOther
            :id="state.vrifiedAssets.length === 0 ? 'assets' : undefined"
            :has-vrified-assets="state.vrifiedAssets.length > 0"
            :assets="state.otherAssets"
          />
          <CompaniesPressReleases :posts="state.posts" @see-more="fetchNextPosts" />
          <div v-if="state.stocks.length || state.capitalStructure" class="striped">
            <div class="container flex flex-col items-start lg_flex-row lg_space-x-6">
              <CompaniesStockOverviewSection
                v-if="state.stocks.length"
                id="stock-overview"
                class="flex-1 w-full"
                :company="state.company"
                :stocks="state.stocks"
                :class="['lg_w-2/3', { 'w-full': !state.capitalStructure }]"
              />
              <CompaniesCapitalStructureSection
                v-if="state.capitalStructure"
                class="w-full lg_w-2/5"
                :capital-structure="state.capitalStructure"
                data-testid="capital-structure-section"
              />
            </div>
          </div>
          <CompaniesInvestorDeckSection class="striped" :decks="state.investorDecks" />
        </div>
      </ClientOnly>
    </div>
  </main>
</template>

<script lang="ts" setup>
import type { Company } from '@/entities/company'
import type { ScheduleSetting } from '@/entities/meeting'
import type { Ref } from 'vue'

const { $analytics, $sentry } = useNuxtApp()
const config = useRuntimeConfig()
const route = useRoute()

const asInvestor = route.query.investor === 'true'
const showInvestorBanner: Ref<boolean> = ref(asInvestor)
const navigationScrollMarker = ref<HTMLElement | undefined>()
const showNavigation = ref(false)
const companyId = Array.isArray(route.params.id) ? route.params.id[0] : route.params.id

const { state, initialize, fetchNextPosts } = useCompany({ id: companyId, omitAuth: asInvestor })
const { setNotification } = useGlobalNotification()

useRedirectFromIdToSlug({
  urlParamId: companyId,
  entity: computed(() => state.value.company),
  redirect: url_slug => navigateTo({ params: { id: url_slug }, query: route.query, replace: true }),
})

function toggleNavbarVisibility() {
  let scrollPadding = parseInt(window.getComputedStyle(document.body).scrollPaddingTop, 10)
  if (isNaN(scrollPadding)) {
    scrollPadding = 0
  }
  const boundingRect = navigationScrollMarker.value?.getBoundingClientRect() ?? false
  const theFirstSectionIsAtTopOfPage = boundingRect && boundingRect.top <= scrollPadding
  showNavigation.value = theFirstSectionIsAtTopOfPage
}

watch(
  () => state.value.error,
  e => {
    if (!e) return

    if ('statusCode' in e && e.statusCode === 404) {
      showError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        message: 'The page you are trying to access does not exist.',
      })
      return
    }

    if ('statusCode' in e && (e.statusCode === 401 || e.statusCode === 403)) {
      return navigateTo({ path: '/discover/companies' })
    }

    $sentry?.captureException(e)
  },
  { immediate: true },
)

watch(
  () => state.value.company,
  async (company: Company | null) => {
    if (!company) return

    $analytics.track('showedScreenCompanyProfile', {
      companyId: company.id,
      companyName: company.name,
    })
  },
  { immediate: true },
)

watch(navigationScrollMarker, () => {
  if (route.hash) {
    setTimeout(() => {
      document.getElementById(route.hash.replace('#', ''))?.scrollIntoView({ behavior: 'smooth', inline: 'nearest' })
    }, 0)
  }
})

onMounted(async () => {
  await initialize()

  document.addEventListener('scroll', toggleNavbarVisibility)

  if (route.query.feedback) {
    setNotification({
      title: 'Thank you for your feedback',
      text: 'Your valuable feedback has been sent to the VRIFY Team to help improve our content for you.',
      delay: 4000,
    })
    navigateTo({ ...route, query: { ...route.query, feedback: undefined } }, { replace: true })
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('scroll', toggleNavbarVisibility)
})

useHead({
  title: () => `${state.value.company?.name} | VRIFY - Make Every Investor Interaction Count`,
  meta: [
    {
      property: 'og:title',
      content: () => `${state.value.company?.name} | VRIFY - Make Every Investor Interaction Count`,
    },
    {
      property: 'og:image',
      content: () => state.value.company?.profile_image ?? '',
    },
    {
      property: 'og:url',
      content: () => `${config.public.WEB_ENDPOINT}companies/${state.value.company?.url_slug}`,
    },
  ],
})
</script>

<style lang="stylus" scoped>
.section-gradient
  @apply pb-14 lg_pb-28
  background: linear-gradient(0deg, rgba(250, 250, 250, 1) 0px, rgba(250, 250, 250, 0) 200px)
  &:first-child
    @apply mt-0

.striped
  @apply pt-14 lg_pt-28
  @extends .section-gradient

@screen lg
  .split-view .container
    padding-right 28.75rem
.investor-banner-visible
  padding-top 6rem
@screen lg
  .investor-banner-visible
    padding-top 8rem
</style>
